<div class="ts-page">
  <app-project-nav></app-project-nav>
  <app-page-header [title]="'testSteps.pageTitle' | translate" [subtitle]="'testSteps.pageSubtitle' | translate">
    <ng-container right>
      <app-stat-group class="ts-header-stats" role="group" [items]="statItems()" [attr.aria-label]="statsAriaLabel()" />
      @if (!isReadOnly()) {
        <button mat-flat-button color="primary" type="button" class="ts-create-btn" (click)="onCreate()">
          <mat-icon aria-hidden="true">add</mat-icon>
          {{ 'testSteps.addButton' | translate }}
        </button>
      }
    </ng-container>
  </app-page-header>

  <div class="ts-toolbar">
    <app-search-bar
      class="ts-search"
      [placeholder]="'testSteps.searchPlaceholder' | translate"
      [value]="searchTerm()"
      (valueChange)="onSearchChange($event)"
    />
    <app-test-step-filter-bar
      [categoryOptions]="categoryOptions()"
      [statusOptions]="statusOptions()"
      [authorOptions]="authorOptions()"
      [selectedCategory]="selectedCategory()"
      [selectedStatus]="selectedStatus()"
      [selectedAuthor]="selectedAuthor()"
      [savedViews]="savedViewsOptions()"
      [activeSavedView]="activeSavedViewName()"
      [projectId]="projectId"
      (filterChange)="onFilterChange($event)"
      (resetFilters)="onResetFilters()"
      (savedViewSelected)="onSavedViewSelected($event)"
      (savedViewSaved)="onSavedViewSaved($event)"
      (savedViewDeleted)="onSavedViewDeleted($event)"
      (savedViewDeleteFailed)="onSavedViewDeleteFailed()"
    />
    <span class="ts-result-count">{{ resultCountKey() | translate: { count: total() } }}</span>
  </div>

  @if (deleteError()) {
    <p class="ts-hint ts-hint--error" role="alert">{{ 'testSteps.deleteError' | translate }}</p>
  }

  @if (savedViewsError()) {
    <p class="ts-hint ts-hint--error" role="alert">{{ savedViewsError() }}</p>
  }

  @if (loading()) {
    <div class="ts-loading">
      <mat-spinner diameter="32" />
    </div>
  } @else if (loadError()) {
    <p class="ts-hint ts-hint--error" role="alert">{{ 'testSteps.loadError' | translate }}</p>
  } @else if (rows().length === 0) {
    <p class="ts-hint">{{ 'testSteps.empty' | translate }}</p>
  } @else if (!hasResults()) {
    <p class="ts-hint ts-hint--filtered">{{ 'testSteps.emptyFiltered' | translate }}</p>
  } @else {
    <div appListFocusScroll class="ts-card" role="table" [attr.aria-label]="'testSteps.tableLabel' | translate">
      <div class="ts-table-header" role="row">
        <div class="ts-col ts-col--id" role="columnheader" [attr.aria-sort]="sortAria('shortId')">
          <button type="button" class="ts-sort-btn" (click)="sortBy('shortId')">
            {{ 'testSteps.table.id' | translate }}
            <mat-icon
              class="ts-sort-btn__icon"
              [class.ts-sort-btn__icon--active]="tableSort().col === 'shortId'"
              aria-hidden="true"
            >
              {{ sortIcon('shortId') }}
            </mat-icon>
          </button>
        </div>
        <div class="ts-col ts-col--title" role="columnheader" [attr.aria-sort]="sortAria('title')">
          <button type="button" class="ts-sort-btn" (click)="sortBy('title')">
            {{ 'testSteps.table.title' | translate }}
            <mat-icon
              class="ts-sort-btn__icon"
              [class.ts-sort-btn__icon--active]="tableSort().col === 'title'"
              aria-hidden="true"
            >
              {{ sortIcon('title') }}
            </mat-icon>
          </button>
        </div>
        <div class="ts-col ts-col--category" role="columnheader" [attr.aria-sort]="sortAria('category')">
          <button type="button" class="ts-sort-btn" (click)="sortBy('category')">
            {{ 'testSteps.table.category' | translate }}
            <mat-icon
              class="ts-sort-btn__icon"
              [class.ts-sort-btn__icon--active]="tableSort().col === 'category'"
              aria-hidden="true"
            >
              {{ sortIcon('category') }}
            </mat-icon>
          </button>
        </div>
        <div class="ts-col ts-col--status" role="columnheader" [attr.aria-sort]="sortAria('status')">
          <button type="button" class="ts-sort-btn" (click)="sortBy('status')">
            {{ 'testSteps.table.status' | translate }}
            <mat-icon
              class="ts-sort-btn__icon"
              [class.ts-sort-btn__icon--active]="tableSort().col === 'status'"
              aria-hidden="true"
            >
              {{ sortIcon('status') }}
            </mat-icon>
          </button>
        </div>
        <div class="ts-col ts-col--usage" role="columnheader" [attr.aria-sort]="sortAria('usageCount')">
          <button type="button" class="ts-sort-btn" (click)="sortBy('usageCount')">
            {{ 'testSteps.table.usage' | translate }}
            <mat-icon
              class="ts-sort-btn__icon"
              [class.ts-sort-btn__icon--active]="tableSort().col === 'usageCount'"
              aria-hidden="true"
            >
              {{ sortIcon('usageCount') }}
            </mat-icon>
          </button>
        </div>
        <div class="ts-col ts-col--author" role="columnheader" [attr.aria-sort]="sortAria('authorName')">
          <button type="button" class="ts-sort-btn" (click)="sortBy('authorName')">
            {{ 'testSteps.table.author' | translate }}
            <mat-icon
              class="ts-sort-btn__icon"
              [class.ts-sort-btn__icon--active]="tableSort().col === 'authorName'"
              aria-hidden="true"
            >
              {{ sortIcon('authorName') }}
            </mat-icon>
          </button>
        </div>
        <div class="ts-col ts-col--actions" role="columnheader">
          {{ 'testSteps.table.actions' | translate }}
        </div>
      </div>

      @for (row of sortedRows(); track row.id; let last = $last) {
        <div class="ts-row" [class.ts-row--last]="last" role="row">
          <div class="ts-col ts-col--id" role="cell">
            <code class="ts-id-pill">{{ row.shortId }}</code>
          </div>
          <div class="ts-col ts-col--title" role="cell">
            <span class="ts-title">{{ row.title }}</span>
          </div>
          <div class="ts-col ts-col--category" role="cell">
            @if (row.category) {
              <span [class]="row.categoryClass">{{ row.categoryLabel }}</span>
            } @else {
              <span class="ts-none">—</span>
            }
          </div>
          <div class="ts-col ts-col--status" role="cell">
            <span [class]="row.statusClass">{{ row.statusLabel }}</span>
          </div>
          <div class="ts-col ts-col--usage" role="cell">
            <span class="ts-usage" [attr.aria-label]="'testSteps.usageAria' | translate: { count: row.usageCount }">
              <span class="ts-usage__count">{{ 'testSteps.usageValue' | translate: { count: row.usageCount } }}</span>
              <span class="ts-usage__unit">{{ 'testSteps.usageUnit' | translate }}</span>
            </span>
          </div>
          <div class="ts-col ts-col--author" role="cell">
            @if (row.authorName) {
              <app-avatar [name]="row.authorName" [size]="28" aria-hidden="true" />
              <span class="ts-author__name">{{ row.authorName }}</span>
            } @else {
              <span class="ts-none" [attr.aria-label]="'testSteps.table.noAuthor' | translate">—</span>
            }
          </div>
          <div class="ts-col ts-col--actions" role="cell">
            @if (!isReadOnly()) {
              <button
                type="button"
                class="ts-action-btn ts-action-btn--accent"
                [attr.aria-label]="'testSteps.edit' | translate"
                (click)="onEdit(row)"
              >
                <mat-icon aria-hidden="true">edit</mat-icon>
              </button>
              <button
                type="button"
                class="ts-action-btn ts-action-btn--danger"
                [attr.aria-label]="'testSteps.delete' | translate"
                (click)="onDeleteRequest(row)"
              >
                <mat-icon aria-hidden="true">delete</mat-icon>
              </button>
            }
          </div>
        </div>
      }
    </div>
  }
</div>

<app-confirm-dialog
  [visible]="showDeleteModal()"
  [title]="'testSteps.deleteTitle' | translate"
  [message]="deleteMessage()"
  [confirmLabel]="'testSteps.deleteConfirm' | translate"
  [cancelLabel]="'testSteps.deleteCancel' | translate"
  [busy]="deleting()"
  (confirmed)="onConfirmDelete()"
  (cancelled)="onCancelDelete()"
/>
